import { jsxs, jsx } from "react/jsx-runtime";
import { useClassNameFactory, useColorScheme } from "@contember/react-utils";
import { deprecate, fallback, isDefined, dataAttribute, themeClassName, colorSchemeClassName } from "@contember/utilities";
import { memo, forwardRef } from "react";
import { Label } from "../Typography/Label.js";
import { Stack } from "../Stack/Stack.js";
import { Text } from "../Typography/Text.js";
const Box = memo(forwardRef(({
  actions,
  align,
  background = true,
  border = true,
  borderRadius = true,
  children,
  className: classNameProp,
  componentClassName = "box",
  direction,
  distinction,
  evenly,
  fit = "width",
  focusRing,
  footer,
  gap = "gutter",
  header,
  heading,
  horizontal,
  intent,
  isActive,
  label,
  justify,
  padding = true,
  reverse,
  wrap,
  ...rest
}, ref) => {
  const className = useClassNameFactory(componentClassName);
  deprecate("1.3.0", padding === "default", '`padding="default"`', "omitted `padding` prop");
  padding = fallback(padding, padding === "default", true);
  deprecate("1.3.0", padding === "no-padding", '`padding="no-padding"`', "`padding={false}`");
  padding = fallback(padding, padding === "no-padding", false);
  deprecate("1.3.0", padding === "with-padding", '`padding="with-padding"`', "`padding={true}`");
  padding = fallback(padding, padding === "with-padding", true);
  deprecate("1.3.0", isDefined(distinction), "the `distinction` prop", "`background={false} border={false} padding={false}`");
  border = fallback(border, distinction === "seamless", false);
  padding = fallback(padding, distinction === "seamless", false);
  deprecate("1.3.0", heading !== void 0, "`heading` prop", "`label` prop");
  label = fallback(label, heading !== void 0, heading);
  return /* @__PURE__ */ jsxs(
    Stack,
    {
      ...rest,
      ref,
      align,
      "data-active": dataAttribute(isActive),
      "data-background": dataAttribute(background),
      "data-border": dataAttribute(border),
      "data-border-radius": dataAttribute(borderRadius),
      "data-focus-ring": dataAttribute(focusRing),
      "data-fit": dataAttribute(fit),
      "data-padding": dataAttribute(padding),
      className: className(null, [
        ...themeClassName(intent),
        colorSchemeClassName(useColorScheme()),
        classNameProp
      ]),
      gap,
      horizontal,
      justify,
      reverse,
      children: [
        header ? /* @__PURE__ */ jsx("div", { className: className("header"), children: header }) : (label || actions) && /* @__PURE__ */ jsxs("div", { className: className("header"), children: [
          typeof label === "string" || typeof label === "number" ? /* @__PURE__ */ jsx(Label, { children: label }) : label,
          actions && /* @__PURE__ */ jsx("div", { className: className("actions"), contentEditable: false, children: actions })
        ] }),
        children && /* @__PURE__ */ jsx(
          Stack,
          {
            className: [className("body"), className("content")],
            evenly,
            gap,
            horizontal,
            align,
            justify,
            children: typeof children === "string" || typeof children === "number" ? /* @__PURE__ */ jsx(Text, { children }) : children
          }
        ),
        footer && /* @__PURE__ */ jsx("div", { className: className("footer"), children: footer })
      ]
    }
  );
}));
Box.displayName = "Box";
export {
  Box
};
//# sourceMappingURL=Box.js.map
